import React, { useEffect, useState, useImperativeHandle } from 'react';
import apis from '@/apis/goodsPool';
import {
  Drawer,
  message,
  Popover,
  Space,
  Table,
  Tabs,
  Tag,
  Descriptions,
  Tooltip,
  Typography,
  Row,
  Col,
  Empty,
} from 'antd';
import moment from 'moment';
import { formatPrice, filterConditions } from '@/common/utils';
import './index.less';
import Delete from '@/components/delete';
import RenderOptions from './components/render-options';
import renderCateList from './components/render-all-category';
import RenderHP_rule, { PopoverRule, SubRuleTabs } from './components/render-HP-rule';
import MicroDrawer from './components/micro-drawer';

const styleOfDot = {
  marginRight: '2px',
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '4px',
  backgroundColor: '#2D64FF',
  lineHeight: '12px',
};

function CategoryInventoryTable(props, ref) {
  const {
    type,
    activityId,
    searchField,
    expandable,
    rowSelection = false,
    goSelect = () => {},
    isOwner,
    refreshBlock,
    log_sense_id,
  } = props;
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState(null); // 数据详情弹窗内容
  const [data, setData] = useState({
    currentPage: 1,
    pageSize: 5,
    totalCount: 0,
    list: [],
  });
  const [conditions, setConditions] = useState({ categories: [], searchField, currentPage: 1, pageSize: 5 });
  const [allRuleDetail, setAllRuleDetail] = useState({}); //表格货盘规则明细缓存
  const [tableKey, setTableKey] = useState(0);

  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (!props.activityId) return;
    apis
      .categoryList({ activityId: props.activityId })
      .then((res) => {
        const { ret, msg, data } = res;
        if (ret !== '0' || !data) throw msg;
        setOptions(data.list);
      })
      .catch((ex) => {
        message.error(ex || '类目列表请求失败！');
      });
  }, [props.activityId]);

  const refresh = () => {
    setConditions({ ...conditions, currentPage: 1 });
  };

  useImperativeHandle(ref, () => {
    return {
      refresh: refresh,
    };
  });

  useEffect(() => {
    setConditions((val) => ({ ...val, searchField, currentPage: 1 }));
  }, [searchField]);

  useEffect(() => {
    if (!type || !activityId) return;
    let didMount = true;
    setLoading(true);
    apis
      .categoryInventoryList({ type, activityId, ...conditions })
      .then((res) => {
        setLoading(false);
        if (!didMount) return;
        const { ret, msg, data } = res;
        if (ret !== '0' || !data) throw msg;
        setData(data);
        setTableKey((val) => val + 1);
      })
      .catch((ex) => {
        message.error(ex || '类目货盘列表请求失败！');
      });
    return () => {
      didMount = false;
    };
  }, [type, activityId, conditions]);

  const del = (params) => {
    apis
      .inventoryOperate({ activityId, ...params })
      .then((res) => {
        const { ret, msg } = res;
        if (ret !== '0') throw msg;
        refreshBlock();
        message.success('移除成功！');
      })
      .catch((ex) => {
        message.error(ex || '移除失败！');
      });
  };

  //获取货盘规则明细，规则列展开更多和展开子表是一个接口，相同的数据
  const getAllRuleDetail = async (inventoryId) => {
    if (allRuleDetail[inventoryId]) return;
    try {
      const res = await apis.queryInventoryRule({ inventoryId: inventoryId, activityId });
      const { ret, msg, data } = res;
      if (ret !== '0' || !data) throw msg;
      const rule = {};
      rule[inventoryId] = data.list;
      setAllRuleDetail((val) => Object.assign({}, val, rule));
    } catch (ex) {
      message.error(ex || '规则明细请求失败！');
    }
  };

  const renderCateTitle = () => {
    return (
      <div>
        类目
        <RenderOptions
          options={options}
          value={conditions.categories}
          onChange={(val) => setConditions({ ...conditions, categories: val, currentPage: 1 })}
        />
      </div>
    );
  };

  const columns = [
    {
      title: '货盘信息',
      width: 160,
      render: (record) => {
        return (
          <div className="PH_info">
            <div>{record.inventoryName}</div>
            {record.receivedTime ? (
              <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                接收于 {moment(record.receivedTime).format('YYYY-MM-DD HH:mm')}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: '创建人',
      dataIndex: 'creatorErp',
      width: 80,
    },
    {
      title: renderCateTitle,
      width: 120,
      render: renderCateList,
    },
    {
      title: '子策略',
      render: (record) => {
        const name = record.subStrategies?.map((item) => item.subStrategyName);
        return name?.join('、') || '';
      },
      width: 120,
    },
    {
      title: '货盘规则',
      render: (record) => (
        <RenderHP_rule
          rules={record.rules}
          extra={
            <PopoverRule
              activityId={activityId}
              haveRuleUpdate={record.haveRuleUpdate}
              rules={allRuleDetail[record.inventoryId]}
              inventoryInfo={record}
            >
              <a onMouseOver={() => getAllRuleDetail(record.inventoryId)}>
                <span>查看明细</span>

                {!!record.haveRuleUpdate && <br />}
                {!!record.haveRuleUpdate && (
                  <span>
                    <i style={styleOfDot} />
                    有更新
                  </span>
                )}
              </a>
            </PopoverRule>
          }
        />
      ),
      width: 350,
    },
    {
      title: 'SKU数量',
      dataIndex: 'skuNum',
      render: (record) => formatPrice(record),
      width: 92,
      align: 'right',
    },
    {
      title: '货盘商品金额',
      dataIndex: 'goodsAmount',
      render: (record) => formatPrice(record),
      width: 120,
      align: 'right',
    },
    {
      title: '操作',
      width: type == 2 ? 140 : 85,
      render: (record) => {
        return (
          <Space>
            <a
              onClick={() => {
                setVisible(true);
                setDetail(record);
              }}
            >
              数据详情
            </a>
            {type == 2 && (
              <>
                {isOwner && (
                  <Delete title="确认移除吗？" onConfirm={() => del({ opt: 3, inventoryIds: [record.inventoryId] })}>
                    <a>移除</a>
                  </Delete>
                )}
                {record.status == 2 ? (
                  <a
                    onClick={() =>
                      goSelect(record.subStrategies, [
                        { inventoryName: record.inventoryName, inventoryId: record.inventoryId },
                      ])
                    }
                  >
                    去选品
                  </a>
                ) : (
                  <Tooltip title="该货盘的数据正在同步中，暂时无法进行选品操作，请稍后再来吧～" placement="topLeft">
                    <a disabled>同步中</a>
                  </Tooltip>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  // 展开子表格
  const expandedRowRender = ({ inventoryId, inventoryName, status }) => {
    const _columns = [
      {
        title: '子策略',
        dataIndex: 'subStrategyName',
        width: 200,
      },
      {
        title: '货盘规则',
        width: 850,
        render: (record) => {
          if (record.rules && record.rules[0] && record.rules[0].ruleDetails) {
            return (
              <RenderHP_rule
                rules={record.rules[0].ruleDetails}
                extra={
                  <PopoverRule content={<SubRuleTabs rules={record.rules} />}>
                    <a>查看全部</a>
                  </PopoverRule>
                }
              />
            );
          }
        },
      },
      {
        title: '操作',
        width: 200,
        render: (record) => {
          return status == 2 ? (
            <a onClick={() => goSelect([record], [{ inventoryId: inventoryId, inventoryName: inventoryName }])}>
              去选品
            </a>
          ) : (
            <Tooltip title="该货盘的数据正在同步中，暂时无法进行选品操作，请稍后再来吧～" placement="topLeft">
              <a disabled>同步中</a>
            </Tooltip>
          );
        },
      },
    ];
    return (
      <Table columns={_columns} dataSource={allRuleDetail[inventoryId]} rowKey="subStrategyId" pagination={false} />
    );
  };
  return (
    <>
      <Table
        key={tableKey}
        className="category-inventory-table"
        columns={columns}
        rowKey="inventoryId"
        loading={loading}
        dataSource={data.list}
        rowSelection={rowSelection}
        locale={{
          emptyText: (
            <Empty style={{ margin: 0 }} image={Empty.PRESENTED_IMAGE_SIMPLE} description="目前暂无品类货盘推送" />
          ),
        }}
        pagination={{
          hideOnSinglePage: true,
          current: conditions.currentPage,
          total: data.totalCount,
          pageSize: conditions.pageSize,
          onChange: (page) => {
            setConditions({ ...conditions, currentPage: page });
          },
        }}
        expandable={
          expandable && {
            columnWidth: '18px',
            expandedRowRender: expandedRowRender,
            onExpand: (expanded, record) => {
              if (expanded) {
                getAllRuleDetail(record.inventoryId);
                window.log('yx_center', 'show_strategy', activityId, log_sense_id, record.inventoryId);
              }
            },
          }
        }
      />
      <Drawer
        placement="bottom"
        title="货盘详情"
        visible={visible}
        height="90%"
        onClose={() => setVisible(false)}
        destroyOnClose={true}
      >
        {detail && (
          <div className="category-inventory-table-HP-detail-drawer">
            <div className="detail-drawer-name-info">
              <div className="__name">
                <Tooltip title={detail.inventoryName}>{detail.inventoryName}</Tooltip>
              </div>
              <div className="__info">创建人：{detail.creatorErp}</div>
              <div className="__info">接收于：{moment(detail.receivedTime).format('YYYY-MM-DD HH:mm')}</div>
            </div>
            <Descriptions
              title="货盘规则"
              extra={
                <PopoverRule placement="bottomLeft" rules={allRuleDetail[detail.inventoryId]}>
                  <a onMouseOver={() => getAllRuleDetail(record.inventoryId)}>查看明细</a>
                </PopoverRule>
              }
              className="detail-drawer-rule-info"
              column={3}
            >
              {detail.rules &&
                detail.rules.length > 0 &&
                detail.rules.map((item, index) => {
                  if (item.type == 1 || item.type == 2) {
                    return (
                      <Descriptions.Item key={index}>
                        {'·'}
                        <span className="detail-drawer-rule-info_item">
                          {item.type == 1 ? '类目' : '部门'}：{item.value.join('，')}
                        </span>
                      </Descriptions.Item>
                    );
                  }
                  return item.value && item.value.length > 0
                    ? item.value.map((ele) => (
                        <Descriptions.Item key={ele}>
                          {'·'}
                          <span className="detail-drawer-rule-info_item">{ele}</span>
                        </Descriptions.Item>
                      ))
                    : null;
                })}
            </Descriptions>
          </div>
        )}
        <MicroDrawer visible={visible} id={detail?.inventoryId} />
      </Drawer>
    </>
  );
}

export default React.forwardRef(CategoryInventoryTable);
